<template>
    <Teleport to="body">
        <Transition name="cal-modal">
            <div
                v-if="show && game"
                class="cal-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="upcoming-match-title"
            >
                <button type="button" class="cal-modal__backdrop" aria-label="Закрыть" @click="$emit('close')" />
                <div class="cal-modal__panel">
                    <header class="cal-modal__header">
                        <h2 id="upcoming-match-title" class="cal-modal__title">Предстоящий матч</h2>
                        <button type="button" class="cal-modal__close" aria-label="Закрыть" @click="$emit('close')">
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
                            </svg>
                        </button>
                    </header>

                    <div class="cal-modal__body">
                        <p class="cal-modal__badge">{{ competitionLabel }}</p>

                        <div class="cal-modal__teams">
                            <div class="cal-modal__team">
                                <div class="cal-modal__logo">
                                    <img v-if="game.home_team?.logo_path" :src="game.home_team.logo_path" alt="" />
                                    <span v-else>{{ teamInitial(game.home_team) }}</span>
                                </div>
                                <span class="cal-modal__team-name">{{ game.home_team?.name }}</span>
                            </div>
                            <div class="cal-modal__vs">vs</div>
                            <div class="cal-modal__team">
                                <div class="cal-modal__logo">
                                    <img v-if="game.away_team?.logo_path" :src="game.away_team.logo_path" alt="" />
                                    <span v-else>{{ teamInitial(game.away_team) }}</span>
                                </div>
                                <span class="cal-modal__team-name">{{ game.away_team?.name }}</span>
                            </div>
                        </div>

                        <dl class="cal-modal__meta">
                            <div>
                                <dt>Дата и время</dt>
                                <dd>{{ game.datetime || '—' }}</dd>
                            </div>
                            <div v-if="game.venue">
                                <dt>Место</dt>
                                <dd>{{ game.venue }}</dd>
                            </div>
                            <div v-if="game.stage_name">
                                <dt>Этап</dt>
                                <dd>{{ game.stage_name }}</dd>
                            </div>
                            <div v-if="game.round_number">
                                <dt>Тур</dt>
                                <dd>{{ game.round_number }}</dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
    show: { type: Boolean, default: false },
    game: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const COMPETITION_LABELS = {
    championship: 'Чемпионат',
    cup: 'Кубок',
    supercup: 'Суперкубок',
    friendly: 'Товарищеский',
};

const competitionLabel = computed(() => {
    if (!props.game) return '';
    return COMPETITION_LABELS[props.game.competition_type] || props.game.competition_type || 'Матч';
});

function teamInitial(team) {
    const name = team?.short_name || team?.name || '?';
    return String(name).charAt(0).toUpperCase();
}

function onKeydown(event) {
    if (event.key === 'Escape' && props.show) {
        emit('close');
    }
}

watch(
    () => props.show,
    (open) => {
        if (typeof document === 'undefined') return;
        document.body.style.overflow = open ? 'hidden' : '';
    },
);

onMounted(() => {
    document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onKeydown);
    if (typeof document !== 'undefined') {
        document.body.style.overflow = '';
    }
});
</script>

<style lang="scss" scoped>
@import '../../../../scss/tokens';

.cal-modal {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
}

.cal-modal__backdrop {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(15, 23, 42, 0.45);
    cursor: pointer;
}

.cal-modal__panel {
    position: relative;
    z-index: 1;
    width: min(100%, 420px);
    background: #fff;
    border-radius: $radius-lg;
    box-shadow: 0 16px 48px rgba(15, 23, 42, 0.2);
    overflow: hidden;
}

.cal-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.1rem;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}

.cal-modal__title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: $color-text-primary;
}

.cal-modal__close {
    border: 0;
    background: transparent;
    color: $color-text-secondary;
    width: 2rem;
    height: 2rem;
    border-radius: $radius-sm;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: $color-brand-alpha-soft;
        color: $color-brand;
    }
}

.cal-modal__body {
    padding: 1.1rem;
}

.cal-modal__badge {
    margin: 0 0 1rem;
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $color-brand;
    background: $color-brand-alpha;
    border-radius: $radius-pill;
    padding: 0.25rem 0.6rem;
}

.cal-modal__teams {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 0.75rem;
    align-items: center;
    margin-bottom: 1.15rem;
}

.cal-modal__team {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    text-align: center;
}

.cal-modal__logo {
    width: 48px;
    height: 48px;
    border-radius: $radius-md;
    background: $color-brand-alpha-soft;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: $color-brand;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
}

.cal-modal__team-name {
    font-size: 0.9rem;
    font-weight: 600;
    color: $color-text-primary;
    line-height: 1.25;
}

.cal-modal__vs {
    font-size: 0.75rem;
    font-weight: 700;
    color: $color-text-light;
    text-transform: uppercase;
}

.cal-modal__meta {
    margin: 0;
    display: grid;
    gap: 0.75rem;

    div {
        display: grid;
        gap: 0.15rem;
    }

    dt {
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.03em;
        color: $color-text-secondary;
    }

    dd {
        margin: 0;
        font-size: 0.95rem;
        color: $color-text-primary;
    }
}

.cal-modal-enter-active,
.cal-modal-leave-active {
    transition: opacity 0.18s ease;
}

.cal-modal-enter-active .cal-modal__panel,
.cal-modal-leave-active .cal-modal__panel {
    transition: transform 0.18s ease, opacity 0.18s ease;
}

.cal-modal-enter-from,
.cal-modal-leave-to {
    opacity: 0;
}

.cal-modal-enter-from .cal-modal__panel,
.cal-modal-leave-to .cal-modal__panel {
    transform: translateY(8px);
    opacity: 0;
}
</style>
